/*
 * @define new creator method for change
 */
import _ from 'underscore';
import { v4 as uuidV4 } from 'uuid';
import { patch, post, remove } from '../ajax';
import { constantsRegistry } from '../../constants/tableConstantsFactory';
import tokenErrorActions from '../tokenErrorActionCreators';
import notificationActions from '../notificationActionCreators';
import notificationConstants from '../../constants/notificationConstants';
export default function changeActionFactory(entity, entityRoute) {
  var _this = this;
  const constants = constantsRegistry(entity);
  const change = (id, path, data) => ({
    type: constants.CHANGE,
    id,
    data,
    path
  });
  const changeQuietly = (id, path, data) => ({
    type: constants.CHANGE_QUIETLY,
    id,
    data,
    path
  });

  // indicates some operation is underway and we should
  // wait to save for instance until it is complete.
  // this allows us to wrap multiple actions into one
  // if needed, such as when we are propagating a change
  // and propagating a log of that change as well.
  const operationPending = () => ({
    type: constants.OPERATION_PENDING
  });
  const operationComplete = () => ({
    type: constants.OPERATION_COMPLETE
  });
  const create = function create(id) {
    let defaultData = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return {
      type: constants.CREATE,
      id,
      defaultData
    };
  };
  const createFail = error => ({
    type: constants.CREATE_FAIL,
    error
  });
  const createSuccess = (id, data) => ({
    type: constants.CREATE_SUCCESS,
    id,
    data
  });
  const destroy = () => ({
    type: constants.DESTROY
  });
  const destroySuccess = ids => ({
    type: constants.DESTROY_SUCCESS,
    ids
  });
  const destroyFail = (ids, error) => ({
    type: constants.DESTROY_FAIL,
    ids,
    error
  });
  const notifyPendingSave = () => notificationActions.info({
    message: 'Please wait, your changes are being saved.',
    id: 'cancel_save_request'
  });
  const save = id => (dispatch, getState) => {
    const list = getState().notification.list;
    if (list && list.length > 0 && list.some(n => n.id === notificationConstants.UNSAVED_CHANGES_WARNING)) {
      // dismiss the "unsaved changes" notification if it is present. the user has decided to
      // take action ("save"), so the prompt is no longer necessary. keeping it around
      // gives the user opportunity to repeatedly press the 'save' button,
      // increasing the likelihood of DEADLOCK and DUPLICATE errors
      // https://www.pivotaltracker.com/story/show/177306477
      // https://www.pivotaltracker.com/story/show/177311226
      dispatch(notificationActions.dismiss(notificationConstants.UNSAVED_CHANGES_WARNING));
    }
    return dispatch({
      type: constants.SAVE,
      id
    });
  };
  const saveFail = function saveFail(id, error) {
    let data = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    return {
      type: constants.SAVE_FAIL,
      id,
      error,
      data
    };
  };
  const saveSuccess = (id, data) => ({
    type: constants.SAVE_SUCCESS,
    id,
    data
  });
  const saveAll = () => ({
    type: constants.SAVE_ALL
  });
  const saveAllFail = error => ({
    type: constants.SAVE_ALL_FAIL,
    error
  });
  const saveAllSuccess = data => ({
    type: constants.SAVE_ALL_SUCCESS,
    data
  });
  const errorMessage = errors => dispatch => {
    dispatch({
      type: 'ERROR',
      errors
    });
    if (errors.length > 0) {
      dispatch(notificationActions.dismiss());
      dispatch(notificationActions.error({
        list: errors
      }, {
        autoClose: 15000
      }));
    }
  };
  const undoChanges = () => ({
    type: constants.UNDO_CHANGES
  });
  const serverDestroy = ids => async dispatch => {
    dispatch(destroy());
    let reqBody = ids;
    if (!_.isArray(ids)) {
      reqBody = [ids];
    }
    try {
      await remove("/".concat(entityRoute), null, reqBody);
      return dispatch(destroySuccess(ids));
    } catch (e) {
      return dispatch(tokenErrorActions.handleError(e, entity, destroyFail.bind(this, ids, e), serverDestroy.bind(this, ids)));
    }
  };
  const _serverSave = function serverSave(entityObject) {
    let retry = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    let retryFunction = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : _serverSave;
    let handleRedirect = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : () => {};
    let postOpts = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : undefined;
    return async (dispatch, getState) => {
      const _getState$entity = getState()[entity],
        loading = _getState$entity.loading,
        pendingChanges = _getState$entity.pendingChanges;
      if (!loading && pendingChanges || retry) {
        const id = entityObject.id ? entityObject.id : uuidV4().toUpperCase();
        dispatch(save(id));
        const url = "/".concat(entityRoute, "/").concat(id);
        try {
          await post(url, entityObject, true, postOpts);
          return dispatch(saveSuccess(id, entityObject));
        } catch (e) {
          return dispatch(tokenErrorActions.handleError(e, entity, saveFail.bind(_this, entityObject.id, e, entityObject), retryFunction.bind(_this, entityObject, true, retryFunction, handleRedirect, postOpts), handleRedirect));
        }
      }
      return dispatch(notifyPendingSave());
    };
  };
  const serverPatch = mergePatch => async (dispatch, getState) => {
    const loading = getState()[entity].loading;
    if (!loading) {
      dispatch(save(mergePatch.id));
      const url = "/".concat(entityRoute, "/").concat(mergePatch.id);
      try {
        const response = await patch(url, mergePatch);
        return dispatch(saveSuccess(mergePatch.id, response.data));
      } catch (e) {
        return dispatch(tokenErrorActions.handleError(e, entity, saveFail.bind(this, mergePatch.id, e), _serverSave.bind(this, mergePatch)));
      }
    }
    return dispatch(notifyPendingSave());
  };
  const serverSaveAll = objArray => async (dispatch, getState) => {
    const loading = getState()[entity].loading;
    if (!loading) {
      dispatch(saveAll(objArray));
      const url = "/".concat(entityRoute);
      try {
        const response = await post(url, objArray);
        return dispatch(saveAllSuccess(response.data));
      } catch (e) {
        return dispatch(tokenErrorActions.handleError(e, entity, saveAllFail.bind(this, e), serverSaveAll.bind(this, objArray)));
      }
    }
    return dispatch(notifyPendingSave());
  };

  // return all our actions.
  return {
    change,
    changeQuietly,
    create,
    createFail,
    createSuccess,
    destroy,
    destroyFail,
    destroySuccess,
    errorMessage,
    operationPending,
    operationComplete,
    save,
    saveFail,
    saveSuccess,
    saveAll,
    saveAllFail,
    saveAllSuccess,
    serverDestroy,
    serverPatch,
    serverSave: _serverSave,
    serverSaveAll,
    undoChanges
  };
}